import Help from '../../core/Help';
import BasicReadField from '../../core/BasicReadField';
import LiteralConfirmationField from '../../core/LiteralConfirmationField';
import IRIdialog from '../../core/IRIdialog';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import BasicLanguageFieldDisplay from '../../core/BasicLanguageFieldDisplay';
import LabelledSwitch from '../LabelledSwitch';
import LanguageChip from '../LanguageChip';
import { globalStyles as styles } from '../style/gstheme';
import TitledPanel from '../TitledPanel';
import { LANGS, MAX_PROJECT_DESC_LENGTH, MAX_PROJECT_TITLE_LENGTH } from '../utils/constants';
import { checkResourceEntitlements } from '../utils/utils';
import ProjectColour from './ProjectColour';

class ProjectBasics extends React.Component {

    render() {

        const { data, addNewIRI, addNewLiteral, onDelete, onUpdate, handleAddProjectLang, handleExcludeFromImportAssociation,
            handleChangeColour, entitlement, handleEnableSkosXL, handleEnableLargeDataSettings } = this.props;

        const { projectLang, description, title, projectColour, defaultLang, projectDatatype } = data;
        const availableLangs = Object.keys(LANGS).filter(lang => !projectLang.includes(lang));
        const isReadOnly = checkResourceEntitlements(data, entitlement);

        return (
            <Grid container spacing={0} style={{ paddingBottom: '30px' }}>
                <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <BasicLanguageFieldDisplay
                                dataTest="project-title"
                                message={"Add Title Translation"}
                                defaultLang={defaultLang}
                                title="Project Title"
                                ariaLabel="Project title"
                                property="title"
                                values={title}
                                projectLang={projectLang}
                                onePerLang={true}
                                placeholder="Enter title text"
                                name="Title"
                                maxLength={MAX_PROJECT_TITLE_LENGTH}
                                onDelete={onDelete}
                                onUpdate={onUpdate}
                                isReadOnly={isReadOnly}
                                onAddLanguage={addNewLiteral} />
                        </Grid>
                        <Grid item xs={12}>
                            <BasicLanguageFieldDisplay
                                dataTest="project-description"
                                message={"Add Description Translation"}
                                defaultLang={defaultLang}
                                title="Project Description"
                                property="description"
                                values={description}
                                projectLang={projectLang}
                                onePerLang={true}
                                required={false}
                                placeholder="Enter description text"
                                name="Description"
                                maxLength={MAX_PROJECT_DESC_LENGTH}
                                onDelete={onDelete}
                                onUpdate={onUpdate}
                                isReadOnly={isReadOnly}
                                onAddLanguage={addNewLiteral} />
                        </Grid>
                        <Grid item xs={12}>
                            <TitledPanel title="Default Project Language">
                                <LanguageChip
                                    dataTest="default-project-language"
                                    lang={defaultLang}
                                    defaultLang={defaultLang} />
                            </TitledPanel>
                        </Grid>
                        <Grid item xs={12}>
                            <TitledPanel dataTest="project-languages" title="Project Languages">
                                {[...projectLang].sort().map(lang =>
                                    <LanguageChip
                                        key={"chip-lang-" + lang}
                                        lang={lang}
                                        onDelete={lang !== defaultLang ?
                                            () => onDelete("projectLang")(lang)
                                            :
                                            null}
                                        defaultLang={defaultLang} />)}
                            </TitledPanel>
                        </Grid>
                        <Grid item xs={12}>
                            <TitledPanel dataTest="available-project-languages" title="Available Project Languages">
                                <div data-test="available-project-language-list" style={{ maxHeight: '200px', overflowX: 'hidden', overflowY: 'scroll' }}>
                                    {availableLangs.length > 0 && availableLangs
                                        .filter(lang => lang !== defaultLang)
                                        .map(lang =>
                                            <LanguageChip key={"chip-" + lang}
                                                lang={lang}
                                                defaultLang={defaultLang}
                                                inactive={isReadOnly}
                                                onClick={!isReadOnly ? () => handleAddProjectLang(lang) : null} />
                                        )}
                                    {availableLangs.length === 0 && <Typography variant="body2">All languages are being used</Typography>}
                                </div>
                            </TitledPanel>
                        </Grid>
                        {data.type.includes("TaxonomyProject") &&
                            <React.Fragment>
                                <Grid item xs={6}>
                                    <TitledPanel title="SKOS-XL">
                                        <LabelledSwitch
                                            dataTest="skosXL"
                                            disabled={isReadOnly || data.skosXL === true}
                                            checked={data.skosXL || false}
                                            handleChange={handleEnableSkosXL}
                                            label="Enabled" />
                                    </TitledPanel>
                                </Grid>
                                <Grid item xs={6}>
                                    <TitledPanel title="Large Data Settings" actions={
                                        <Help textRef="LARGE_DATA" position={false} />
                                    }>
                                        <LabelledSwitch
                                            dataTest="largeDataSettings"
                                            disabled={isReadOnly}
                                            checked={data.largeDataSettings || false}
                                            handleChange={handleEnableLargeDataSettings}
                                            label="Enabled" />
                                    </TitledPanel>
                                </Grid>
                            </React.Fragment>
                        }
                        {data.type.includes("DataProject") &&
                            <React.Fragment>
                                <Grid item xs={12}>
                                    <TitledPanel title="Large Data Settings" actions={
                                        <Help textRef="LARGE_DATA" position={false} />
                                    }>
                                        <LabelledSwitch
                                            dataTest="largeDataSettings"
                                            disabled={isReadOnly}
                                            checked={data.largeDataSettings || false}
                                            handleChange={handleEnableLargeDataSettings}
                                            label="Enabled" />
                                    </TitledPanel>
                                </Grid>
                            </React.Fragment>
                        }
                        <Grid item xs={12}>
                            <TitledPanel dataTest="custom-datatypes" height="full" title="Custom Datatypes" actions=
                                {!isReadOnly ?
                                    <IRIdialog
                                        dataTest="custom-datatype"
                                        onAddIRI={addNewIRI("projectDatatype")}
                                        existingValues={projectDatatype}
                                        title="Add a Datatype"
                                        placeholder="Enter datatype IRI"
                                        name="IRI (http or https)" />
                                    :
                                    null
                                }>
                                {projectDatatype && projectDatatype.map((datatype, index) =>
                                    <LiteralConfirmationField
                                        dataTest={"datatype-" + index}
                                        displayDatatype={false}
                                        key={datatype}
                                        margin="dense"
                                        text={datatype}
                                        datatype="http://www.w3.org/2001/XMLSchema#anyURI"
                                        existingValues={projectDatatype}
                                        canBeDeleted={true}
                                        onUpdate={onUpdate("projectDatatype", null, index)}
                                        onDelete={onDelete("projectDatatype", null, index)}
                                        isReadOnly={isReadOnly} />
                                )}
                            </TitledPanel>
                        </Grid>
                        {data.type.includes("OntologyProject") &&
                            <Grid item xs={12}>
                                <TitledPanel title="Import Settings">
                                    <LabelledSwitch
                                        dataTest="exclude-from-import"
                                        disabled={isReadOnly}
                                        checked={data.excludeFromImportAssociation || false}
                                        handleChange={handleExcludeFromImportAssociation}
                                        label="Exclude from Import Association" />
                                </TitledPanel>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <TitledPanel title="Project Colour" >
                                <ProjectColour isReadOnly={isReadOnly} colour={projectColour} handleChangeColour={handleChangeColour} />
                            </TitledPanel>
                        </Grid>
                        <Grid item xs={12}>
                            <TitledPanel title="Project IRI">
                                <BasicReadField ariaLabel="Project IRI" dataTest="iri" fullWidth text={data.id} />
                            </TitledPanel>
                        </Grid>
                        <Grid item xs={12}>
                            <TitledPanel title="Project Graph IRI">
                                <BasicReadField ariaLabel="Project graph IRI" dataTest="graph-iri" fullWidth text={data.inNamedGraph} />
                            </TitledPanel>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
}

ProjectBasics.propTypes = {
    addNewIRI: PropTypes.func.isRequired,
    addNewLiteral: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    handleAddProjectLang: PropTypes.func.isRequired,
    entitlement: PropTypes.string.isRequired,
    handleChangeColour: PropTypes.func.isRequired,
    handleEnableSkosXL: PropTypes.func.isRequired,
    handleEnableLargeDataSettings: PropTypes.func.isRequired,
    handleExcludeFromImportAssociation: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
};

export default withStyles(styles)(ProjectBasics);